import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { consumeJustLoggedIn, getWelcomeEnabled, greetingFor } from '../lib/welcome';

// Fires once per sign-in (see lib/welcome). Rendered once at the app-routes
// level so it survives the owner/principal hop from the hub picker into the
// dashboard without re-triggering. Non-blocking top toast — the app stays
// interactive underneath; tap the card (or wait ~3.5s) to dismiss.
export default function WelcomeGreeting() {
  const { role } = useAuth();
  const [g, setG] = useState(null);       // greeting content, or null when hidden
  const [shown, setShown] = useState(false); // drives the slide-in/out transition

  // Consume the one-shot flag the first time we have a role after login.
  useEffect(() => {
    if (!role) return;
    if (!consumeJustLoggedIn()) return;   // read-and-clear; only a fresh sign-in
    if (!getWelcomeEnabled(role)) return; // toggled off for this role
    setG(greetingFor(role));
  }, [role]);

  // Slide in next frame, auto-dismiss after a beat, unmount after the fade.
  useEffect(() => {
    if (!g) return undefined;
    const raf = requestAnimationFrame(() => setShown(true));
    const outT = setTimeout(() => setShown(false), 3400);
    const doneT = setTimeout(() => setG(null), 3820);
    return () => { cancelAnimationFrame(raf); clearTimeout(outT); clearTimeout(doneT); };
  }, [g]);

  if (!g) return null;

  function dismiss() { setShown(false); setTimeout(() => setG(null), 400); }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 9600, padding: '0 16px' }}>
      <div
        onClick={dismiss}
        role="status"
        aria-live="polite"
        style={{
          pointerEvents: 'auto',
          cursor: 'pointer',
          marginTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          minWidth: 250,
          maxWidth: 'calc(100vw - 32px)',
          padding: '14px 18px',
          background: 'var(--card, #17181b)',
          border: '1px solid var(--border, #2a2c31)',
          borderRadius: 14,
          boxShadow: 'var(--shadow, 0 12px 40px rgba(0,0,0,0.4))',
          transform: shown ? 'translateY(0)' : 'translateY(-18px)',
          opacity: shown ? 1 : 0,
          transition: 'transform .38s cubic-bezier(.2,.8,.2,1), opacity .38s ease',
          fontFamily: 'Inter, sans-serif',
        }}>
        <div style={{ fontSize: 30, lineHeight: 1 }} aria-hidden="true">{g.emoji}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: 'var(--muted, #9a9ca1)', textTransform: 'uppercase' }}>{g.eyebrow}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text, #e9e9ec)', marginTop: 2 }}>{g.part}.</div>
          <div style={{ fontSize: 13, color: 'var(--muted, #9a9ca1)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.line}</div>
        </div>
      </div>
    </div>
  );
}
